import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Flex,
  Icon,
} from '@kognitos/lattice';
import { useGetAutomationSchedule } from '@/generated/hooks/grimoire/v1/api';
import type {
  TDayOfWeek,
  TRepeatFrequency,
  // TScheduleEndCondition,
  TSchedulePreset,
} from './types';
// TODO: DO NOT ENABLE ScheduleEndsCard until backend tracks execution count
// Backend edge case #1: AfterOccurrences execution count tracking
// - The scheduler is stateless and cannot track how many times a schedule has executed
// - When end_type = AFTER_OCCURRENCES, the filter passes through (no enforcement)
// - Grimoire service needs to check/decrement remaining_occurrences in InvokeScheduledAutomation handler using PostgreSQL
// - Enabling this UI before backend support will mislead users - schedules will run indefinitely
// See: Schedule Edge Cases Analysis plan for full details
// import { ScheduleEndsCard } from './ScheduleEndsCard';
import { ScheduleActionButtons } from './ScheduleActionButtons';
import { SchedulePreview } from './SchedulePreview';
import { ScheduleRepeatsCard } from './ScheduleRepeatsCard';
import {
  getDefaultScheduleConfig,
  isConfigValidForPreview,
  parseScheduleConfig,
} from './utils';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { isNotFoundError } from '@/shared/utils';

/**
 * Schedule configuration state.
 */
export interface IScheduleConfig {
  startDate?: Date;
  timezone?: string;
  interval?: number;
  frequency?: TRepeatFrequency;
  selectedDays?: TDayOfWeek[];
  selectedPreset?: TSchedulePreset;
  repeatStartTime?: string;
  // TODO: Re-enable when backend supports active_window
  // repeatEndTime?: string;
  /** i.e. 2nd Monday of the month */
  nthWeekday?: number;
  // TODO: DO NOT ENABLE until backend tracks execution count in PostgreSQL
  // Backend edge case: Scheduler is stateless and cannot track occurrences
  // Grimoire service needs to check/decrement remaining_occurrences in InvokeScheduledAutomation handler
  // See: Schedule Edge Cases Analysis plan for details
  // endCondition?: TScheduleEndCondition;
  // endDate?: Date;
  // maxInvocations?: number;
}

/**
 * Props for the ScheduleTriggersDialog component.
 */
export interface IScheduleTriggersDialogProps {
  /** ID of the automation to schedule */
  automationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * A dialog component for configuring schedule triggers.
 * Contains a card for configuring the start date and how the schedule
 * repeats, alongside a preview of upcoming runs.
 *
 * @param props - Component props
 * @returns JSX element containing the schedule triggers dialog
 */
export function ScheduleTriggersDialog({
  automationId,
  open,
  onOpenChange,
}: IScheduleTriggersDialogProps) {
  const { t } = useTranslation(['automation', 'common']);

  // Get org and workspace from store
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Fetch existing schedule to determine if one exists (only when dialog is open)
  const { data: existingScheduleResponse, error: existingScheduleError } =
    useGetAutomationSchedule(
      currentOrganizationId,
      currentWorkspaceId,
      automationId,
      {
        query: {
          enabled: open,
          retry: false,
        },
      },
    );

  const existingSchedule = isNotFoundError(existingScheduleError)
    ? undefined
    : existingScheduleResponse?.data;

  // Schedule configuration state - starts as blank slate, populated from API if schedule exists
  const [config, setConfig] = useState<IScheduleConfig>(
    getDefaultScheduleConfig,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Populate form from existing schedule when fetched
  // eslint-disable-next-line no-restricted-syntax -- reason: Need to sync form state when API data loads
  useEffect(() => {
    if (existingSchedule?.config) {
      setConfig(parseScheduleConfig(existingSchedule));
    }
  }, [existingSchedule]);

  // Determine if a schedule exists
  const hasSchedule = !!existingSchedule;
  // Determine if the schedule is enabled (not paused)
  const isScheduleEnabled = hasSchedule && existingSchedule.enabled !== false;

  // View mode: schedule exists and user is not editing
  const isViewMode = hasSchedule && !isEditing;

  const handleClose = () => {
    onOpenChange(false);
  };

  const isValidConfig = isConfigValidForPreview(config);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full sm:max-w-[888px] h-[696px] flex flex-col overflow-hidden"
        data-testid="schedule-triggers-dialog"
      >
        {/* Header */}
        <DialogHeader>
          <Flex direction="row" align="center" gap={3}>
            <DialogTitle className="font-medium">
              {t('automation.schedule.title')}
            </DialogTitle>

            {/* Status badges */}
            {isSaving && (
              <Badge variant="secondary" data-testid="schedule-saving-badge">
                <Icon type="Loader2" size="sm" className="animate-spin" />
                {t('automation.schedule.badge.saving')}
              </Badge>
            )}

            {/* Is active */}
            {hasSchedule && !isSaving && isScheduleEnabled && (
              <Badge
                className="bg-success/10 text-success border-transparent"
                data-testid="schedule-active-badge"
              >
                {t('automation.schedule.badge.active')}
              </Badge>
            )}

            {/* Is paused */}
            {hasSchedule && !isSaving && !isScheduleEnabled && (
              <Badge variant="destructive" data-testid="schedule-paused-badge">
                {t('automation.schedule.badge.paused')}
              </Badge>
            )}
          </Flex>
          <DialogDescription>
            {t('automation.schedule.description')}
          </DialogDescription>
        </DialogHeader>

        {/* Panel Content */}
        <Flex
          flex="1"
          className="rounded-md bg-secondary min-h-0"
          padding={5}
          width="full"
        >
          {/* Schedule Wrapper - Two column layout */}
          <Flex direction="row" height="full" width="full" gap={5}>
            {/* Left Column - Schedule Configuration Cards */}
            <Flex
              className="rounded-xl overflow-hidden"
              height="full"
              width="full"
            >
              <Flex
                direction="column"
                gap={4}
                height="full"
                width="full"
                overflow="auto"
                className="shrink-0 max-w-[504px] min-h-0"
              >
                <ScheduleRepeatsCard
                  date={config.startDate}
                  interval={config.interval}
                  frequency={config.frequency}
                  selectedDays={config.selectedDays}
                  selectedPreset={config.selectedPreset}
                  repeatStartTime={config.repeatStartTime}
                  timezone={config.timezone}
                  onChange={changes =>
                    setConfig(prev => ({ ...prev, ...changes }))
                  }
                  readonly={isViewMode}
                />

                {/* TODO: DO NOT ENABLE until backend supports end conditions
                     Backend edge case: Scheduler cannot track execution count
                     See warning comments at top of file for details */}
                {/* <ScheduleEndsCard
                  // endCondition={config.endCondition}
                  // endDate={config.endDate}
                  // maxInvocations={config.maxInvocations}
                  onEndConditionChange={cond =>
                    setConfig(prev => ({ ...prev, endCondition: cond }))
                  }
                  onEndDateChange={date => setConfig(prev => ({ ...prev, endDate: date }))}
                  onMaxInvocationsChange={count =>
                    setConfig(prev => ({ ...prev, maxInvocations: count }))
                  }
                /> */}
              </Flex>
            </Flex>

            {/* Right Column - Schedule Preview */}
            <Flex className="min-h-0 w-[280px]" height="full">
              <SchedulePreview
                scheduleConfig={isValidConfig ? config : undefined}
                organizationId={currentOrganizationId}
                workspaceId={currentWorkspaceId}
                automationId={automationId}
              />
            </Flex>
          </Flex>
        </Flex>

        {/* Footer */}
        <DialogFooter>
          <ScheduleActionButtons
            organizationId={currentOrganizationId}
            workspaceId={currentWorkspaceId}
            automationId={automationId}
            config={config}
            isConfigValid={isValidConfig}
            isViewMode={isViewMode}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            isSaving={isSaving}
            setIsSaving={setIsSaving}
            onClose={handleClose}
            isScheduleEnabled={isScheduleEnabled}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
